import { MouseEvent, useEffect, useState } from "react";
import RightTimeApi from "@/API";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import '@style/Admin.scss';
import AdminTitle from "./AdminTitle";

import Enums from "@/enums";
import FeatureFlags from "@/utils/FeatureFlags";

import CompanyDashboardTab from "./CompanyDashboardTab";
import CompanyInfoTab from "./CompanyInfoTab";
import CompanyJobsTab from "./CompanyJobsTab";
import CompanyDepartmentsTab from "./CompanyDepartmentsTab";
import CompanyRecruitTab from "./CompanyRecruitTab";
import CompanyJobMatchTab from "./CompanyJobMatchTab";
import CompanyCultureTab from "./CompanyCultureTab";
import CompanyImagesTab from "./CompanyImagesTab";
import CompanySitesTab from "./CompanySitesTab";
import CompanyUsersTab from "./CompanyUsersTab";
import CompanyRolesTab from "./CompanyRolesTab";
import CompanyTestTypesTab from "./CompanyTestTypesTab";
import CompanyRecentsTab from "./CompanyRecentsTab";
import CompanyRequestTab from "./CompanyRequestTab";
import CompanyContentTab from "./CompanyContentTab";
import CompanyOrgChartTab from "./CompanyOrgChartTab";
import CompanyBillingTab from "@acct/tabs/CompanyBillingTab";
import CompanyEmailTab from "./CompanyEmailTab";
import HelpMenuTab from "./HelpMenuTab";
import CompanyReportsTab from "./CompanyReportsTab";
import Helpers from "../Helpers";
import { readPermIdsFromSession } from "@/i18n/sessionUser";

const FLASH_ERROR_KEY = "AppFlashError";

export default function Company(props: any) {
  const [company, updateCompany] = useState<any>(null);
  const { companyIdStr } = useParams();
  const [companyId, updateCompanyId] = useState(0);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const [backUrl, updateBackUrl] = useState("");
  const [activeTab, updateActiveTab] = useState<string | null>(queryParams.get("tab"));
  const navigate = useNavigate();
  const [showSpinner, updateShowSpinner] = useState(false);
  const [adminPerms, updateAdminPerms] = useState<number[]>([]);

  useEffect(() => {
    updateAdminPerms(readPermIdsFromSession());
  }, []);

  useEffect(() => {
    // console.log("got to Company.jsx");
    if (companyIdStr) updateCompanyId(parseInt(companyIdStr));
  }, []);

  useEffect(() => {
    if (companyId == 0 || !company || !company.id) {
      getCompanyInfoAsync();
    }
  }, [companyId]);

  useEffect(() => {
    let urlParams = "";
    if (companyId) {
      urlParams = "/Company/" + companyId;
      if (activeTab) 
        urlParams = urlParams + "?tab=" + activeTab;
      navigate(urlParams);
    }
  }, [activeTab]);

  function onChangeTab(e: MouseEvent<HTMLSpanElement>) {
    const newTab = e.currentTarget.dataset.tab;
    if (newTab && newTab !== activeTab) {
      // console.log(`Changing tab to: ${newTab}`);
      updateActiveTab(newTab);
      navigate(`?tab=${newTab}`);
    }
  }

  async function getCompanyInfoAsync() {
    if (companyId) {
      updateShowSpinner(true);
      const resp = await RightTimeApi.getAsync("/CompanyInfo2/" + companyId);
      updateShowSpinner(false);
      if (resp && resp.ok && resp.jsonData) {
        updateCompany(resp.jsonData);
        //debugger;
        if (resp.jsonData.canAccessAdmin)
          updateBackUrl("/Admin?tab=companies");
        const styleDeclaration = document.documentElement.style;
        resp.jsonData.colorScheme.schemeElements.forEach((e) => {
          styleDeclaration.setProperty(e.variable, e.colorValue);
        });
        if (!activeTab) {
          if (resp.jsonData.permissions.some(p => p.companyId === companyId && p.permissionId === 254))  // SystemAdmin
            updateActiveTab("company");
          else if (resp.jsonData.permissions.some(p => p.companyId === companyId && p.permissionId === Enums.ROLE_PERMS.CompanyMenuTestRecents))
            updateActiveTab("recents");
          else if (resp.jsonData.permissions.some(p => p.companyId === companyId && p.permissionId === Enums.ROLE_PERMS.AnonTestTaker))
            updateActiveTab("request");
        }
      } else {
        if (resp && resp.status === 401) {
          navigate("/");
        } else {
          window.sessionStorage.setItem(FLASH_ERROR_KEY, "The requested company no longer exists");
          // Navigate back to where the user came from
          if (backUrl) {
            navigate(backUrl);
          } else {
            navigate(-1);
          }
        }
      }
    }
  }

  let tabData: {
    company: any;
    activeTab: string | null;
    updateSpinner: typeof updateShowSpinner;
    adminPerms: number[];
    updateActiveTab: typeof updateActiveTab;
    refreshCompany?: () => Promise<void>;
  } = {
    company: company,
    activeTab: activeTab,
    updateSpinner: updateShowSpinner,
    adminPerms: adminPerms,
    updateActiveTab: updateActiveTab
  };

  const canShowDashboardTab = !!company && (
    company.permissions.some(p => p.companyId === company.id && p.permissionId === Enums.ROLE_PERMS.DashboardViewable) ||
    company.permissions.some(p => p.companyId === 1 && p.permissionId === Enums.ROLE_PERMS.SystemAdmin)
  );
  const canShowCompanyTab = !!company && (
    company.permissions.some(p => p.companyId === company.id && p.permissionId === Enums.ROLE_PERMS.EditCompany) ||
    company.permissions.some(p => p.companyId === 1 && p.permissionId === Enums.ROLE_PERMS.SystemAdmin) ||
    company.permissions.some(p => p.companyId === company.id && p.permissionId === Enums.ROLE_PERMS.CompanyMenuCompany)
  );
  const canShowSitesTab = !!company && (
    company.permissions.some(p => p.companyId === company.id && p.permissionId === Enums.ROLE_PERMS.CompanyMenuSites) ||
    company.permissions.some(p => p.companyId === 1 && p.permissionId === Enums.ROLE_PERMS.SystemAdmin)
  );
  const canShowUsersTab = !!company && (
    company.permissions.some(p => p.companyId === company.id && p.permissionId === Enums.ROLE_PERMS.CompanyMenuUsers) ||
    company.permissions.some(p => p.companyId === 1 && p.permissionId === Enums.ROLE_PERMS.SystemAdmin)
  );
  const canShowRolesTab = !!company && (
    company.permissions.some(p => p.companyId === company.id && p.permissionId === Enums.ROLE_PERMS.CompanyMenuRoles) ||
    company.permissions.some(p => p.companyId === 1 && p.permissionId === Enums.ROLE_PERMS.SystemAdmin)
  );
  const canShowJobsTab = !!company && (
    company.permissions.some(p => p.companyId === company.id && p.permissionId === Enums.ROLE_PERMS.CompanyMenuJobs) ||
    company.permissions.some(p => p.companyId === 1 && p.permissionId === Enums.ROLE_PERMS.SystemAdmin)
  );
  const canShowDepartmentsTab = !!company && company.departmentsEnabled && (
    company.permissions.some(p => p.companyId === company.id && p.permissionId === Enums.ROLE_PERMS.CompanyMenuDepartments) ||
    company.permissions.some(p => p.companyId === 1 && p.permissionId === Enums.ROLE_PERMS.SystemAdmin)
  );
  const canShowRecruitTab = !!company && (
    company.permissions.some(p => p.companyId === company.id && p.permissionId === Enums.ROLE_PERMS.CompanyMenuCompatibility) ||
    company.permissions.some(p => p.companyId === 1 && p.permissionId === Enums.ROLE_PERMS.SystemAdmin)
  );
  const canShowJobMatchTab = !!company && (
    company.permissions.some(p => p.companyId === company.id && p.permissionId === Enums.ROLE_PERMS.CompanyMenuJobMatch) ||
    company.permissions.some(p => p.companyId === 1 && p.permissionId === Enums.ROLE_PERMS.SystemAdmin)
  );
  const canShowTestsTab = !!company && (
    company.permissions.some(p => p.companyId === company.id && p.permissionId === Enums.ROLE_PERMS.CompanyMenuTestTypes) ||
    company.permissions.some(p => p.companyId === 1 && p.permissionId === Enums.ROLE_PERMS.SystemAdmin)
  );
  const canShowEmailTab = canShowTestsTab;
  const canShowRecentsTab = !!company && (
    company.permissions.some(p => p.companyId === company.id && p.permissionId === Enums.ROLE_PERMS.CompanyMenuTestRecents) ||
    company.permissions.some(p => p.companyId === 1 && p.permissionId === Enums.ROLE_PERMS.SystemAdmin) ||
    company.permissions.some(p => p.companyId === company.id && p.permissionId === Enums.ROLE_PERMS.CompanyAdmin)
  );
  const canShowRequestTab = !!company && (
    company.permissions.some(p => p.companyId === company.id && p.permissionId === Enums.ROLE_PERMS.CompanyMenuTestRequest) ||
    company.permissions.some(p => p.companyId === company.id && p.permissionId === Enums.ROLE_PERMS.AnonTestTaker) ||
    company.permissions.some(p => p.companyId === 1 && p.permissionId === Enums.ROLE_PERMS.SystemAdmin) ||
    company.permissions.some(p => p.companyId === company.id && p.permissionId === Enums.ROLE_PERMS.CompanyAdmin)
  );
  const canShowBillingTab = !!company && company.billable && (
    company.permissions.some(p => p.companyId === 1 && p.permissionId === Enums.ROLE_PERMS.SystemAdmin) ||
    company.permissions.some(p => p.companyId === company.id && p.permissionId === Enums.ROLE_PERMS.CompanyMenuBilling)
  );
  const canShowHelpTab = !!company && (
    company.permissions.some(p => p.companyId === company.id && p.permissionId === Enums.ROLE_PERMS.CompanyMenuHelp) ||
    company.permissions.some(p => p.companyId === 1 && p.permissionId === Enums.ROLE_PERMS.SystemAdmin)
  );
  const canShowReportsTab = !!company && (
    company.permissions.some(p => p.companyId === company.id && p.permissionId === Enums.ROLE_PERMS.CompanyMenuReports) ||
    company.permissions.some(p => p.companyId === 1 && p.permissionId === Enums.ROLE_PERMS.SystemAdmin)
  );
  const isSystemAdmin = !!company && company.permissions.some(
    p => p.companyId === 1 && p.permissionId === Enums.ROLE_PERMS.SystemAdmin,
  );

  const visibleTabs = [
    { id: "dashboard", visible: canShowDashboardTab },
    { id: "company", visible: canShowCompanyTab },
    { id: "sites", visible: canShowSitesTab },
    { id: "users", visible: canShowUsersTab },
    { id: "roles", visible: canShowRolesTab },
    { id: "departments", visible: canShowDepartmentsTab },
    { id: "jobs", visible: canShowJobsTab },
    { id: "recruit", visible: canShowRecruitTab },
    { id: "jobMatch", visible: canShowJobMatchTab },
    { id: "tests", visible: canShowTestsTab },
    { id: "email", visible: canShowEmailTab },
    { id: "recents", visible: canShowRecentsTab },
    { id: "request", visible: canShowRequestTab },
    { id: "companyBilling", visible: canShowBillingTab },
    { id: "help", visible: canShowHelpTab },
    { id: "reports", visible: canShowReportsTab }
  ];

  useEffect(() => {
    if (!company) return;

    const firstVisibleTab = visibleTabs.find((tab) => tab.visible)?.id || null;
    if (!activeTab && firstVisibleTab) {
      updateActiveTab(firstVisibleTab);
      return;
    }

    if (activeTab) {
      const isActiveTabVisible = visibleTabs.some((tab) => tab.visible && tab.id === activeTab);
      if (!isActiveTabVisible) {
        updateActiveTab(firstVisibleTab ?? null);
      }
    }
  }, [company, activeTab, canShowDashboardTab, canShowCompanyTab, canShowSitesTab, canShowUsersTab, canShowRolesTab, canShowDepartmentsTab, canShowJobsTab, canShowRecruitTab, canShowJobMatchTab, canShowTestsTab, canShowEmailTab, canShowRecentsTab, canShowRequestTab, canShowBillingTab, canShowHelpTab, canShowReportsTab]);

  const effectiveActiveTab = company && activeTab
    ? (visibleTabs.some((tab) => tab.visible && tab.id === activeTab)
      ? activeTab
      : (visibleTabs.find((tab) => tab.visible)?.id ?? null))
    : activeTab;

  tabData = {
    company: company,
    activeTab: effectiveActiveTab,
    updateSpinner: updateShowSpinner,
    adminPerms: adminPerms,
    updateActiveTab: updateActiveTab,
    refreshCompany: getCompanyInfoAsync,
  };

  const visibleTabCount = [
    canShowDashboardTab,
    canShowCompanyTab,
    canShowSitesTab,
    canShowUsersTab,
    canShowRolesTab,
    canShowDepartmentsTab,
    canShowJobsTab,
    canShowRecruitTab,
    canShowJobMatchTab,
    canShowTestsTab,
    canShowEmailTab,
    canShowRecentsTab,
    canShowRequestTab,
    canShowBillingTab,
    canShowHelpTab
  ].filter(Boolean).length;

  return (
    <>
      {/* Overlay only renders when showSpinner is true */}
      {showSpinner && (
        <div className="overlay">
          <div className="spinner"></div>
        </div>
      )}
      {company ? (
        <div className="adminPage company">
          <AdminTitle
            backUrl={backUrl}
            company={company}
            activeTab={activeTab}
            updateSpinner={updateShowSpinner}
            helpScope="company"
          />
          <div className="pageContent">
            {visibleTabCount > 1 ? <div className="tabHeader">
              {company && (
                company.permissions.some(p => p.companyId === company.id && p.permissionId === Enums.ROLE_PERMS.DashboardViewable) ||
                company.permissions.some(p => p.companyId === 1 && p.permissionId === Enums.ROLE_PERMS.SystemAdmin))
                ? (
                  <span
                    className={activeTab === "dashboard" ? "tab active" : "tab"}
                    data-tab="dashboard"
                    onClick={onChangeTab}
                  >
                    Dashboard
                  </span>
                ) : null}
              {company && (
                company.permissions.some(p => p.companyId === company.id && p.permissionId === Enums.ROLE_PERMS.EditCompany) ||
                company.permissions.some(p => p.companyId === 1 && p.permissionId === Enums.ROLE_PERMS.SystemAdmin) ||
                company.permissions.some(p => p.companyId === company.id && p.permissionId === Enums.ROLE_PERMS.CompanyMenuCompany))
                ? (
                  <span
                    className={activeTab === "company" ? "tab active" : "tab"}
                    data-tab="company"
                    onClick={onChangeTab}
                  >
                    Company
                  </span>
                ) : null}


              {/* {company && (
                company.permissions.some(p => p.companyId === company.id && p.permissionId === Enums.ROLE_PERMS.EditCompany) ||
                company.permissions.some(p => p.companyId === 1 && p.permissionId === Enums.ROLE_PERMS.SystemAdmin))
                ? (
                  <span
                    className={activeTab === "content" ? "tab active" : "tab"}
                    name="content"
                    onClick={onChangeTab}
                  >
                    Content
                  </span>
                ) : null} */}


              {(company && (
                company.permissions.some(p => p.companyId === company.id && p.permissionId === Enums.ROLE_PERMS.CompanyMenuSites) ||
                company.permissions.some(p => p.companyId === 1 && p.permissionId === Enums.ROLE_PERMS.SystemAdmin)))
                ? (
                  <span
                    className={activeTab === "sites" ? "tab active" : "tab"}
                    onClick={onChangeTab}
                    data-tab="sites"
                  >
                    {company.subEntityTypeName}
                  </span>
                ) : null}

              {(company && (
                company.permissions.some(p => p.companyId === company.id && p.permissionId === Enums.ROLE_PERMS.CompanyMenuUsers) ||
                company.permissions.some(p => p.companyId === 1 && p.permissionId === Enums.ROLE_PERMS.SystemAdmin) ))
                ? (
                  <span
                    className={activeTab === "users" ? "tab active" : "tab"}
                    data-tab="users"
                    onClick={onChangeTab}
                  >
                    Users
                  </span>
                ) : null}
              {/* {(()=>{debugger})()} */}

              {(company && (
                company.permissions.some(p => p.companyId === company.id && p.permissionId === Enums.ROLE_PERMS.CompanyMenuRoles) ||
                company.permissions.some(p => p.companyId === 1 && p.permissionId === Enums.ROLE_PERMS.SystemAdmin)))
                ? (
                  <span
                    className={activeTab === "roles" ? "tab active" : "tab"}
                    data-tab="roles"
                    onClick={onChangeTab}
                  >
                    Roles
                  </span>
                ) : null}


              {canShowDepartmentsTab ? (
                  <span
                    className={activeTab === "departments" ? "tab active" : "tab"}
                    data-tab="departments"
                    onClick={onChangeTab}
                  >
                    Departments
                  </span>
                ) : null}


              {(company && (
                company.permissions.some(p => p.companyId === company.id && p.permissionId === Enums.ROLE_PERMS.CompanyMenuJobs) ||
                company.permissions.some(p => p.companyId === 1 && p.permissionId === Enums.ROLE_PERMS.SystemAdmin)))
                ? (
                  <span
                    className={activeTab === "jobs" ? "tab active" : "tab"}
                    data-tab="jobs"
                    onClick={onChangeTab}
                  >
                    Jobs
                  </span>
                ) : null}

              {company && (
                company.permissions.some(p => p.companyId === company.id && p.permissionId === Enums.ROLE_PERMS.CompanyMenuCompatibility) ||
                company.permissions.some(p => p.companyId === 1 && p.permissionId === Enums.ROLE_PERMS.SystemAdmin))
                ? (
                  <span
                    className={activeTab === "recruit" ? "tab active" : "tab"}
                    data-tab="recruit"
                    onClick={onChangeTab}
                  >
                    Compatibility
                  </span>
                ) : null}

              {canShowJobMatchTab ? (
                  <span
                    className={activeTab === "jobMatch" ? "tab active" : "tab"}
                    data-tab="jobMatch"
                    onClick={onChangeTab}
                  >
                    Job Match
                  </span>
                ) : null}

              {/* {company && (
                company.permissions.some(p => p.companyId === company.id && p.permissionId === Enums.ROLE_PERMS.CompanyCulture) ||
                company.permissions.some(p => p.companyId === 1 && p.permissionId === Enums.ROLE_PERMS.SystemAdmin))
                ? (
                  <span
                    className={activeTab === "culture" ? "tab active" : "tab"}
                    name="culture"
                    onClick={onChangeTab}
                  >
                    Culture
                  </span>
                ) : null} */}


              {/* {(company && (
                company.permissions.some(p => p.companyId === company.id && p.permissionId === Enums.ROLE_PERMS.CompanyMenuImages) ||
                company.permissions.some(p => p.companyId === 1 && p.permissionId === Enums.ROLE_PERMS.SystemAdmin)))
                ? (
                  <span
                    className={activeTab === "images" ? "tab active" : "tab"}
                    name="images"
                    onClick={onChangeTab}
                  >
                    Images
                  </span>
                ) : null} */}


              {(company && (
                company.permissions.some(p => p.companyId === company.id && p.permissionId === Enums.ROLE_PERMS.CompanyMenuTestTypes) ||
                company.permissions.some(p => p.companyId === 1 && p.permissionId === Enums.ROLE_PERMS.SystemAdmin)))
                ? (
                  <span
                    className={activeTab === "tests" ? "tab active" : "tab"}
                    data-tab="tests"
                    onClick={onChangeTab}
                  >
                    TestTypes
                  </span>
                ) : null}

              {canShowEmailTab ? (
                  <span
                    className={activeTab === "email" ? "tab active" : "tab"}
                    data-tab="email"
                    onClick={onChangeTab}
                  >
                    Email
                  </span>
                ) : null}


              {company && (
                company.permissions.some(p => p.companyId === company.id && p.permissionId === Enums.ROLE_PERMS.CompanyMenuTestRecents) ||
                company.permissions.some(p => p.companyId === 1 && p.permissionId === Enums.ROLE_PERMS.SystemAdmin) ||
                company.permissions.some(p => p.companyId === company.id && p.permissionId === Enums.ROLE_PERMS.CompanyAdmin)
              )
                ? (
                  <span
                    className={activeTab === "recents" ? "tab active" : "tab"}
                    onClick={onChangeTab}
                    data-tab="recents"
                  >
                    Recents
                  </span>
                ) : null}


              {company && (
                company.permissions.some(p => p.companyId === company.id && p.permissionId === Enums.ROLE_PERMS.CompanyMenuTestRequest) ||
                company.permissions.some(p => p.companyId === 1 && p.permissionId === Enums.ROLE_PERMS.SystemAdmin) ||
                company.permissions.some(p => p.companyId === company.id && p.permissionId === Enums.ROLE_PERMS.CompanyAdmin))
                ? (
                  <span
                    className={activeTab === "request" ? "tab active" : "tab"}
                    onClick={onChangeTab}
                    data-tab="request"
                  >
                    Request
                  </span>
                ) : null}

              {/* {company && (
                company.permissions.some(p => p.companyId === company.id && p.permissionId === Enums.ROLE_PERMS.EditCompany) ||
                company.permissions.some(p => p.companyId === 1 && p.permissionId === Enums.ROLE_PERMS.SystemAdmin) ||
                company.permissions.some(p => p.companyId === company.id && p.permissionId === Enums.ROLE_PERMS.CompanyMenuTestOrgChart)
              ) ? (
                <span
                  className={activeTab === "orgchart" ? "tab active" : "tab"}
                  onClick={onChangeTab}
                  name="orgchart"
                >
                  Org Chart
                </span>) : null} */}

              {company && company.billable && (
                company.permissions.some(p => p.companyId === 1 && p.permissionId === Enums.ROLE_PERMS.SystemAdmin) ||
                company.permissions.some(p => p.companyId === company.id && p.permissionId === Enums.ROLE_PERMS.CompanyMenuBilling)
              ) ? (
                <span
                  className={activeTab === "companyBilling" ? "tab active" : "tab"}
                  onClick={onChangeTab}
                  data-tab="companyBilling"
                >
                  Billing
                </span>) : null}

              {canShowHelpTab ? (
                <span
                  className={activeTab === "help" ? "tab active" : "tab"}
                  onClick={onChangeTab}
                  data-tab="help"
                >
                  Help
                </span>
              ) : null}

              {canShowReportsTab ? (
                <span
                  className={activeTab === "reports" ? "tab active" : "tab"}
                  onClick={onChangeTab}
                  data-tab="reports"
                >
                  Reports
                </span>
              ) : null}

            </div> : null}
            {visibleTabCount > 1 ? <hr /> : null}
            <div className="tabContent">
              {company && company.permissions.length === 0 ? (
                <span>You do not have access to any data at this time.</span>
              ) : (
                <>
                  <CompanyDashboardTab tabData={tabData} />
                  <CompanyInfoTab tabData={tabData} />
                  <CompanyContentTab tabData={tabData} />
                  <CompanyDepartmentsTab tabData={tabData} />
                  <CompanyJobsTab tabData={tabData} />
                  <CompanyRecruitTab tabData={tabData} />
                  <CompanyJobMatchTab tabData={tabData} />
                  <CompanyCultureTab tabData={tabData} />
                  <CompanySitesTab tabData={tabData} />
                  <CompanyUsersTab tabData={tabData} />
                  <CompanyTestTypesTab tabData={tabData} />
                  <CompanyEmailTab tabData={tabData} />
                  <CompanyRolesTab tabData={tabData} />
                  <CompanyImagesTab tabData={tabData} />
                  <CompanyRecentsTab tabData={tabData} />
                  <CompanyRequestTab tabData={tabData} />
                  <CompanyOrgChartTab tabData={tabData} />
                  <CompanyBillingTab tabData={tabData} />
                  <HelpMenuTab tabData={tabData} helpSection="company.help" isSystemAdmin={isSystemAdmin} />
                  <CompanyReportsTab tabData={tabData} />
                </>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}

